import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Params, Router } from '@angular/router';
import moment from 'moment';
import { Subject, defer } from 'rxjs';
import { takeUntil, finalize, flatMap, tap } from 'rxjs/operators';
import { OpfoelgningWithServiceordreDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/opfoelgning-with-serviceordre-dto';
import { ServiceordreDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/serviceordre-dto';
import { OpfoelgningService } from 'src/app/api/services';
import { OpfoelgningAction, OpfoelgningDialogData, OpfoelgningDialogResponse } from 'src/app/components/aftale/models';
import { EGIS_SERVICE_CALLER } from 'src/app/constants';
import { NotificationType } from 'src/app/notifications/models/notification-models';
import { NotificationService } from 'src/app/notifications/services/notification.service';
import { AuthService } from 'src/app/services/auth.service';
import { ErrorHandlerService } from 'src/app/services/error-handler.service';
import { LoaderService } from 'src/app/services/loader.service';
import { SignalrService } from 'src/app/services/signalr.service';
import { OpfoelgningDialogComponent } from '../opfoelgning-dialog/opfoelgning-dialog.component';
import { PaginationCacheService } from 'src/app/services/paginationcache.service';
import { GetServiceordreOpfoelgningerResponse } from 'src/app/api/models/OK/Egis/Service/Models/Response/get-serviceordre-opfoelgninger-response';
import { inOutAnimation } from 'src/app/common/animations';
import { ServiceordreStatusEnum } from 'src/app/api/models/OK/Egis/Service/Models/Entities/Enums/serviceordre-status-enum';

@Component({
  selector: 'app-opfoelgning-serviceordre',
  templateUrl: './opfoelgning-serviceordre.component.html',
  styleUrls: ['./opfoelgning-serviceordre.component.scss'],
  animations: [inOutAnimation],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class OpfoelgningServiceordreComponent implements OnInit, OnDestroy, AfterViewInit {
  isAdmin: boolean = false;
  canWrite: boolean = false;
  globalActiveRowID: string = '';
  selectedordre: ServiceordreDto;
  serviceordreStatusEnum = ServiceordreStatusEnum;

  displayedColumns: string[] = [
    'serviceordre.produkt',
    'opfoelgning',
    'parked',
    'serviceordre.serviceordreno',
    'priority',
    'deadline',
    'user',
    'serviceordre.okKundeAftaleNr',
    'serviceordre.serviceordreType',
    'status',
    'serviceordre.partner.name',
    'modifiedOn',
    'createdOn',
  ];

  showForAllUsers: boolean = false;
  opfoelgninger: OpfoelgningWithServiceordreDto[] = [];
  rowCount: number;
  pageCount: number;

  unsubscribe$ = new Subject<void>();

  dataSource: MatTableDataSource<OpfoelgningWithServiceordreDto> = new MatTableDataSource<OpfoelgningWithServiceordreDto>();

  parkerede: MatTableDataSource<ServiceordreDto> = new MatTableDataSource<ServiceordreDto>();

  constructor(
    private opfoelgningService: OpfoelgningService,
    private router: Router,
    private route: ActivatedRoute,
    private loader: LoaderService,
    private errorHandler: ErrorHandlerService,
    private auth: AuthService,
    private signalR: SignalrService,
    private dialog: MatDialog,
    private notification: NotificationService,
    private paginationCache: PaginationCacheService,
  ) {
    this.dataSource = new MatTableDataSource();
    this.isAdmin = this.auth.isAdministrator;
    this.canWrite = this.auth.canWrite;
  }

  @ViewChild(MatSort) sort: MatSort;

  ngOnInit(): void {
    this.route.queryParams
      .pipe(
        flatMap((queryParams: Params) => {
          const showForAllUsers = queryParams['showForAllUsers'];
          const orderBy = queryParams['orderBy'];
          const orderDir = queryParams['orderDir'];
          if (showForAllUsers) {
            this.showForAllUsers = JSON.parse(showForAllUsers);
          }
          if (orderBy && orderBy !== '') {
            this.orderBy = orderBy;
          }

          if (orderDir && orderDir !== '') {
            this.orderDir = orderDir;
          }
          this.resetDataAndPagination();
          const pagination = this.paginationCache.position;
          if (pagination) {
            this.page = Number(pagination.page);
            this.prevRow = pagination.id;
          }
          return this.getServiceordreOpfoelgninger$.pipe(
            tap((r: GetServiceordreOpfoelgningerResponse) => {
              this.skip = this.page - 1;
            }),
          );
        }),
      )
      .subscribe(
        () => {},
        (error) => {
          this.errorHandler.HandleError(error);
        },
      );

    this.signalR.opfoelgningChanged$
      .pipe(
        flatMap(() => {
          this.resetDataAndPagination();
          return this.getServiceordreOpfoelgninger$;
        }),
        takeUntil(this.unsubscribe$),
      )
      .subscribe(
        () => {},
        (error) => {
          this.errorHandler.HandleError(error);
        },
      );

    this.loader.loadingStateChanged$.subscribe((isLoading) => {
      this.isLoading = isLoading;
    });
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.unsubscribe();
  }

  ngAfterViewInit() {
    this.dataSource.sort = this.sort;
  }

  getServiceordreOpfoelgninger$ = defer(() => {
    this.loader.show();
    let top = this.page - this.skip;
    let skip = this.skip > this.page ? top - 1 : this.skip;
    return this.opfoelgningService
      .getServiceordreOpfoelgninger({
        body: {
          showForAllUsers: this.showForAllUsers,
          pageTop: top < 1 ? 1 : top,
          pageSkip: skip < 0 ? 0 : skip,
          orderBy: this.orderBy,
          orderDir: this.orderDir,
        },
        caller: EGIS_SERVICE_CALLER,
      })
      .pipe(
        tap((response) => {
          this.rowCount = response.rowCount;
          this.pageCount = response.pageCount;
          this.opfoelgninger = this.opfoelgninger.concat(response.opfoelgninger);
          this.dataSource = new MatTableDataSource(this.opfoelgninger);
        }),
        finalize(() => {
          this.loader.hide();
        }),
        takeUntil(this.unsubscribe$),
      );
  });

  onRowDblClick(row: any) {
    this.router.navigate(['/ordre', row.serviceordre.serviceordreNo, 'edit']);
  }

  getProductIcon(serviceordre: ServiceordreDto) {
    return serviceordre.produkt.toLowerCase();
  }

  opfoelgningClick(event: Event, opfoelgning: OpfoelgningWithServiceordreDto) {
    event.stopPropagation();
    const data: OpfoelgningDialogData = {
      entityNo: opfoelgning.serviceordre.serviceordreNo,
      serviceordre: opfoelgning.serviceordre,
      opfoelgninger: opfoelgning.serviceordre.opfoelgningList,
    };

    let dialogRef = this.dialog.open(OpfoelgningDialogComponent, {
      width: '600px',
      maxWidth: '95vw',
      data,
    });

    dialogRef.afterClosed().subscribe((response: OpfoelgningDialogResponse) => {
      if (response) {
        this.onOpfoelgningChanged(response);
      }
    });
  }

  deadline(opfoelgning: OpfoelgningWithServiceordreDto) {
    const daysToDeadline = moment(opfoelgning.deadline).endOf('day').diff(moment(), 'days');
    if (daysToDeadline <= 0) {
      return 'attention-box attention-box--red';
    } else if (daysToDeadline <= 7) {
      return 'attention-box attention-box--yellow';
    }
    return '';
  }

  onOpfoelgningChanged(response: OpfoelgningDialogResponse) {
    if (response.action == OpfoelgningAction.Upsert) {
      this.loader.show();

      this.opfoelgningService
        .upsertServiceordreOpfoelgning({
          caller: EGIS_SERVICE_CALLER,
          body: {
            id: response.opfoelgning.id,
            note: response.opfoelgning.note,
            deadline: response.opfoelgning.deadline ? new Date(response.opfoelgning.deadline).toOkDateString() : null,
            prioritet: response.opfoelgning.priority,
            entityNo: response.opfoelgning.entityNo,
            parker: response.park,
          },
        })
        .pipe(
          finalize(() => {
            this.loader.hide();
          }),
          takeUntil(this.unsubscribe$),
        )
        .subscribe(
          () => {
            this.notification.success('Opfølgning er gemt.');
          },
          (error) => {
            this.errorHandler.HandleError(error);
          },
        );
    } else if (response.action == OpfoelgningAction.Delete) {
      this.opfoelgningService
        .removeServiceordreOpfoelgning({ caller: EGIS_SERVICE_CALLER, id: response.opfoelgning.id })
        .pipe(
          finalize(() => {
            this.loader.hide();
          }),
          takeUntil(this.unsubscribe$),
        )
        .subscribe(
          () => {
            this.notification.success('Opfølgning er fjernet.');
          },
          (error) => {
            this.errorHandler.HandleError(error);
          },
        );
    }
  }

  onShowForAllUsersToggle() {
    this.resetDataAndPagination();
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { showForAllUsers: this.showForAllUsers },
      queryParamsHandling: 'merge',
    });
  }

  more() {
    this.page += 1;
    this.skip += 1;
    this.loader.show();
    (this.getServiceordreOpfoelgninger$
      .pipe(
        finalize(() => {
          this.loader.hide();
        }),
      )
      .subscribe(),
      (error) => {
        this.errorHandler.HandleError(error);
        this.page -= 1;
      });
  }

  sortData(sort: Sort) {
    this.resetDataAndPagination();

    const orderParams: Params = {
      orderBy: sort.active ? sort.active : null,
      orderDir: sort.direction ? sort.direction : null,
    };

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: orderParams,
      queryParamsHandling: 'merge',
    });
  }

  resetDataAndPagination() {
    this.opfoelgninger = [];
    this.skip = 0;
    this.page = 1;
    this.prevRow = null;
  }

  skip: number = 0;
  page: number = 1;
  orderBy: 'modifiedOn';
  orderDir: 'desc';
  prevRow: number = null;
  isLoading: boolean = false;
  highLight(id: number) {
    const row = this.opfoelgninger.find((p) => p.serviceordre.serviceordreNo == id);
    if (row) this.globalActiveRowID = row.serviceordre.id.toString();
  }
}
